import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

const ImportContext = createContext(null);

// Processing phases for better UX feedback
const PHASES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',      // Phase 1: Upload et analyse OCR
  RECONCILING: 'reconciling',  // Phase 2: Rapprochement automatique
  SYNCING: 'syncing',          // Phase 3: Synchronisation des statuts
  DONE: 'done'
};

export function ImportProvider({ children }) {
  const { token } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingPhase, setProcessingPhase] = useState(PHASES.IDLE);
  const [progress, setProgress] = useState({ current: 0, total: 0, phase: 1, totalPhases: 3 });
  const [lastImportResult, setLastImportResult] = useState(null);
  
  // Use ref to track if processing is ongoing (persists across renders)
  const processingRef = useRef(false);

  const startImport = useCallback(async (files, importPeriode = "") => {
    if (processingRef.current) {
      toast.warning("Un import est déjà en cours");
      return null;
    }

    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return null;
    }

    processingRef.current = true;
    setIsProcessing(true);
    setProcessingPhase(PHASES.UPLOADING);
    setProcessingStatus("Démarrage de l'import...");
    setProgress({ current: 0, total: pdfFiles.length, phase: 1, totalPhases: 3 });
    setLastImportResult(null);

    const results = [];
    const errors = [];
    const periodesImportees = new Set();

    // Helper function to poll for task completion
    const pollTaskStatus = async (taskId, filename) => {
      const maxAttempts = 120; // 2 minutes max (1 second intervals)
      let attempts = 0;
      
      while (attempts < maxAttempts) {
        try {
          const statusRes = await axios.get(`${API}/api/reconciliation/import/status/${taskId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const { status, progress, message, result, error } = statusRes.data;
          
          // Update UI with progress
          setProcessingStatus(`📄 ${message || 'Traitement en cours...'}`);
          
          if (status === 'success') {
            return { success: true, data: result };
          } else if (status === 'error') {
            return { success: false, error: error || 'Erreur inconnue' };
          }
          
          // Still processing - wait and retry
          await new Promise(resolve => setTimeout(resolve, 1000));
          attempts++;
        } catch (pollError) {
          console.error('Poll error:', pollError);
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }
      }
      
      return { success: false, error: 'Timeout - l\'import prend trop de temps' };
    };

    // ========== PHASE 1: Import et Analyse OCR ==========
    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      const shortName = file.name.length > 40 ? file.name.substring(0, 37) + '...' : file.name;
      setProcessingStatus(`📄 Envoi: ${shortName}...`);
      setProgress({ current: i + 1, total: pdfFiles.length, phase: 1, totalPhases: 3 });

      const formData = new FormData();
      formData.append('file', file);
      if (importPeriode) {
        formData.append('periode', importPeriode);
      }

      try {
        // Use async import endpoint
        const res = await axios.post(`${API}/api/reconciliation/import/async`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 30000  // 30 seconds to start the task
        });
        
        const taskId = res.data.task_id;
        setProcessingStatus(`📄 Analyse IA: ${shortName} (peut prendre 1-2 min)...`);
        
        // Poll for completion
        const pollResult = await pollTaskStatus(taskId, file.name);
        
        if (pollResult.success) {
          results.push(pollResult.data);
          
          const periode = pollResult.data.summary?.periode || pollResult.data.periode;
          if (periode) {
            periodesImportees.add(periode);
          }
          
          const docType = pollResult.data.type === 'releve_bancaire' ? 'Relevé' : 'Remise SEPA';
          toast.success(`${docType} importé`, {
            description: `Période: ${periode}`,
            duration: 3000
          });
        } else {
          errors.push(file.name);
          toast.error(`Erreur: ${file.name}`, {
            description: pollResult.error || "Impossible de traiter ce fichier"
          });
        }
      } catch (error) {
        console.error(`Error importing ${file.name}:`, error);
        errors.push(file.name);
        toast.error(`Erreur: ${file.name}`, {
          description: error.response?.data?.detail || "Impossible de traiter ce fichier"
        });
      }
    }

    // ========== PHASE 2: Rapprochement Automatique ==========
    if (periodesImportees.size > 0 && results.length > 0) {
      setProcessingPhase(PHASES.RECONCILING);
      const periodesArray = Array.from(periodesImportees);
      
      for (let i = 0; i < periodesArray.length; i++) {
        const periode = periodesArray[i];
        setProcessingStatus(`🔗 Rapprochement: ${periode}`);
        setProgress({ current: i + 1, total: periodesArray.length, phase: 2, totalPhases: 3 });
        
        try {
          const periodeForUrl = periode.replace("/", "-");
          await axios.post(
            `${API}/api/reconciliation/run/${periodeForUrl}`,
            null,
            { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
          );
        } catch (err) {
          console.error(`Reconciliation error for ${periode}:`, err);
          // Don't show error toast, reconciliation might have already been done during import
        }
      }

      // ========== PHASE 3: Synchronisation des Statuts ==========
      setProcessingPhase(PHASES.SYNCING);
      setProcessingStatus("🔄 Synchronisation des statuts...");
      setProgress({ current: 0, total: periodesArray.length, phase: 3, totalPhases: 3 });
      
      for (let i = 0; i < periodesArray.length; i++) {
        const periode = periodesArray[i];
        setProgress({ current: i + 1, total: periodesArray.length, phase: 3, totalPhases: 3 });
        
        try {
          const periodeForUrl = periode.replace("/", "-");
          await axios.get(
            `${API}/api/reconciliation/sync-statuses/${periodeForUrl}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          // Sync errors are not critical
          console.warn(`Sync warning for ${periode}:`, err);
        }
      }
    }

    // ========== DONE ==========
    setProcessingPhase(PHASES.DONE);
    
    const result = {
      success: results.length > 0,
      results,
      errors,
      periodes: Array.from(periodesImportees),
      releves: results.filter(r => r.document_type === 'releve' || r.type === 'releve_bancaire').length,
      remises: results.filter(r => r.document_type === 'remise' || r.type === 'remise_sepa').length
    };

    setLastImportResult(result);
    setIsProcessing(false);
    setProcessingStatus("");
    setProcessingPhase(PHASES.IDLE);
    processingRef.current = false;

    // Final notification
    if (result.success) {
      const details = [];
      if (result.releves > 0) details.push(`${result.releves} relevé(s)`);
      if (result.remises > 0) details.push(`${result.remises} remise(s)`);
      
      toast.success("✅ Import et rapprochement terminés", {
        description: `${details.join(', ')} • ${result.periodes.length} période(s) traitée(s)`,
        duration: 5000
      });
    }

    if (errors.length > 0) {
      toast.error(`${errors.length} fichier(s) en erreur`, {
        description: errors.join(', ')
      });
    }

    return result;
  }, [token]);

  const clearLastResult = useCallback(() => {
    setLastImportResult(null);
  }, []);

  return (
    <ImportContext.Provider value={{
      isProcessing,
      processingStatus,
      processingPhase,
      progress,
      lastImportResult,
      startImport,
      clearLastResult,
      PHASES
    }}>
      {children}
    </ImportContext.Provider>
  );
}

export function useImport() {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error("useImport must be used within ImportProvider");
  }
  return context;
}
